import React from 'react';
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  useNavigate,
  useParams,
  useRouter,
  type ErrorComponentProps,
} from '@tanstack/react-router';
import { RouterProvider as AriaRouterProvider } from 'react-aria-components';
import * as Sentry from '@sentry/react';
import { AppShell } from './ui/layout/AppShell.js';
import { Settings } from './ui/pages/Settings.js';
import { Properties } from './ui/pages/Properties';
import {
  PropertyDetail,
  PropertyCodeView,
  PropertyCredentialsView,
  PropertyEnvironmentsView,
  PropertyInboxView,
  PropertySettingsView,
  PropertyAdvancedView,
  PropertyContextView,
} from './ui/pages/PropertyDetail';
import { KnownPageAnalysisDetail } from './ui/pages/KnownPageAnalysisDetail.js';
import { Debug } from './ui/pages/Debug.js';
import { TestLoginMCPPreview } from './ui/pages/TestLoginMCPPreview';
import { UIDemo } from './ui/pages/UIDemo';
import { StorageStateDemo } from './ui/pages/StorageStateDemo';
import { Issues } from './ui/pages/Issues';
import { PrReviews } from './ui/pages/PrReviews';
import { Tickets } from './ui/pages/Tickets';
import { PrReviewDetail } from './ui/pages/PrReviewDetail';
import { DigestPage } from './ui/pages/DigestPage';
import { FlowDesigner } from './ui/pages/FlowDesigner';
import { FlowList } from './ui/pages/FlowList';
import { Lookup } from './ui/pages/Lookup';
import { ContextLibraryPage } from './features/context-library';
import { createPromptLibraryRoutes } from './features/prompt-library';
import { createUtilitiesRoutes } from './features/utilities';
import { Attachments } from './ui/pages/Attachments';
import { PublicGist } from './ui/pages/PublicGist';
import {
  SuperadminLayout,
  SuperadminJobs,
  SuperadminAgentRuns,
  SuperadminMetrics,
  CacheTelemetryDashboard,
  SuperadminVerifications,
  SuperadminScheduled,
  SuperadminFeatureFlags,
  SuperadminKnobs,
  SuperadminOrgQuotas,
  SuperadminTestTemplates,
  SuperadminTraceUploads,
  SuperadminDedup,
  SuperadminAbout,
  SuperadminIssueReviews,
  BenchmarkPage,
  SuperadminBenchmarkConfigs,
  SuperadminWorkflows,
  SuperadminOrganizations,
  SuperadminDashboard,
  SuperadminHints,
  SuperadminSupport,
  SuperadminDeploymentAlerts,
  SuperadminBuilderSessions,
  SuperadminAuditLog,
  SuperadminUsers,
  SuperadminTokenUsage,
  SuperadminUserEvents,
  SuperadminMarketingLeads,
} from './ui/pages/superadmin';
import { ApiCatalog } from './ui/pages/ApiCatalog';
import { ApiRequestDetailPage } from './ui/pages/api-catalog/ApiRequestDetailPage';
import { Pages } from './ui/pages/Pages';
import { PagesLayout } from './ui/pages/PagesLayout';
import { PageDetail } from './ui/pages/PageDetail';
import { RunsLayout } from './ui/pages/runs/RunsLayout';
import { SuitesLayout } from './ui/pages/suites/SuitesLayout';
import { SuitesListPage } from './ui/pages/suites/SuitesListPage';
import { SuiteBuilderPage } from './ui/pages/suites/SuiteBuilderPage';
import { VisualPage } from './ui/pages/visual/VisualPage';
import { PerfPage } from './ui/pages/perf/PerfPage';
import { PerfTestPage } from './ui/pages/perf/PerfTestPage';
import { PerfRunPage } from './ui/pages/perf/PerfRunPage';
import { AgentEvalsPage } from './ui/pages/agent-evals/AgentEvalsPage';
import { AgentEvalPage } from './ui/pages/agent-evals/AgentEvalPage';
import { AgentEvalRunPage } from './ui/pages/agent-evals/AgentEvalRunPage';
import { parsePerfStatus, parsePerfVerdict, type PerfSearch } from './features/perf/perfSearch';
import { VisualCheckpointPage } from './ui/pages/visual/VisualCheckpointPage';
import {
  parseVisualSource,
  parseVisualStatus,
  type VisualSearch,
} from './features/visual/visualSearch';
import { ExploratoryTestsPage } from './ui/pages/runs/ExploratoryTestsPage';
import { UxReviewPage } from './ui/pages/runs/UxReviewPage';
import { SpecReviewPage } from './ui/pages/runs/SpecReviewPage';
import { RequireFeature } from './ui/components/RequireFeature';
import { ExploratoryTestDetail } from './ui/pages/ExploratoryTestDetail';
import { TestRunsPage } from './ui/pages/runs/TestRunsPage';
import { QaRunsPage } from './ui/pages/runs/QaRunsPage';
import { validateQaRunsSearch, validateTestRunsSearch } from './ui/pages/runs/runsRouteSearch';
import { RunDetail } from './ui/pages/RunDetail';
import { AgenticRunDetail } from './ui/pages/AgenticRunDetail';
import { ExternalRunDetail } from './ui/pages/ExternalRunDetail';
import { ExternalTestItemDetail } from './ui/pages/ExternalTestItemDetail';
import { isRunAreaState, type RunAreaState } from './features/external-tests/useExternalRun';
import { ExternalTestsCatalog } from './ui/pages/ExternalTestsCatalog';
import { TestRunRedirect } from './ui/pages/TestRunRedirect';
import { RunDetailPage } from './ui/pages/RunDetailPage/RunDetailPage';
import { Releases } from './ui/pages/Releases';
import { ReleaseDetail } from './ui/pages/ReleaseDetail';
import { ReleasesLayout } from './features/releases/pages/ReleasesLayout';
import { ReleasesIndex } from './features/releases/pages/ReleasesIndex';
import { ReleasePropertyTimeline } from './features/releases/pages/ReleasePropertyTimeline';
import { TasksIndex, TaskDetail } from './features/tasks';
import { CycleDetailPage } from './features/exploration/pages/CycleDetailPage';
import { ProductMapPage, validateProductMapSearch } from './features/product-map';
import { BoardsLayout } from './features/boards/BoardsLayout';
import { ProductBoardPage } from './features/product-board/pages/ProductBoardPage';
import { AccountsBoardPage } from './features/accounts/pages/AccountsBoardPage';
import { HomeDashboard } from './ui/pages/HomeDashboard';
import { TablePatternDemo } from './ui/pages/TablePatternDemo';
import { DocsAssistant } from './ui/pages/DocsAssistant';
import { CliLogin } from './ui/pages/CliLogin';
import { createAuthRoutes } from './features/auth/routes';
import { createProofRoutes } from './features/proofs/routes';
import { createSandboxRoutes } from './features/sandboxes/routes';
import { SettingsLayout } from './ui/pages/SettingsLayout';
import { TestDataLayout } from './ui/pages/TestDataLayout';
import { TestDataPage } from './ui/pages/TestDataPage';
import {
  TestDataPropertyDetail,
  TDPropertyCodeView,
  TDPropertyCredentialsView,
  TDPropertyEnvironmentsView,
  TDPropertyInboxView,
  TDPropertyContextView,
  TDPropertySettingsView,
  TDPropertyAdvancedView,
} from './ui/pages/TestDataPropertyDetail';
export type { SandboxDetailSearchParams } from './features/sandboxes/routes';
export type { ResetPasswordSearchParams, VerifyEmailSearchParams } from './features/auth/routes';

/**
 * Bridges react-aria's link/navigation system to TanStack Router. With this in
 * place, react-aria elements rendered with an `href` (e.g. `DataTable` rows via
 * `getRowHref`) navigate client-side on a plain left-click, while a cmd/ctrl-click
 * opens the href in a new tab (react-aria dispatches a synthetic anchor click).
 */
const RootComponent: React.FC = () => {
  const router = useRouter();
  return (
    <AriaRouterProvider
      // eslint-disable-next-line unicorn/no-useless-fallback-in-spread -- the `?? {}` is load-bearing: react-aria's RouterOptions param is `T | undefined` and tsc rejects spreading it without the object fallback (TS2698).
      navigate={(to, options) => router.navigate({ to, ...(options ?? {}) })}
      useHref={(to) => router.buildLocation({ to }).href}
    >
      <AppShell>
        <Outlet />
      </AppShell>
    </AriaRouterProvider>
  );
};

function RouteErrorComponent({ error }: ErrorComponentProps) {
  React.useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-8">
      <div className="text-center max-w-md">
        <h1 className="text-2xl font-bold mb-4">Something went wrong</h1>
        <p className="text-muted-foreground mb-4">An error occurred while loading this page.</p>
        <pre className="text-sm text-left bg-muted p-4 rounded overflow-auto mb-4">
          {error.message}
        </pre>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-primary text-primary-foreground rounded hover:opacity-90"
        >
          Refresh page
        </button>
      </div>
    </div>
  );
}

// ── Shared search param parsers ──────────────────────────────────────────────
const parseSearchNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  if (typeof value === 'string') {
    const parsed = parseInt(value, 10);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return undefined;
};

const parseSearchString = (value: unknown): string | undefined => {
  const stringValue =
    typeof value === 'string'
      ? value
      : typeof value === 'number' && Number.isFinite(value)
        ? String(value)
        : undefined;
  const trimmed = stringValue?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : undefined;
};

const parseSearchIsoDate = (value: unknown): string | undefined => {
  const str = parseSearchString(value);
  if (!str) return undefined;
  const d = new Date(str);
  return Number.isNaN(d.getTime()) ? undefined : str;
};

const parseSearchArray = (value: unknown): string[] | undefined => {
  if (Array.isArray(value)) {
    // Coerce numbers too — the default search parser JSON-parses numeric
    // tokens (e.g. priority=2) into numbers.
    return value
      .filter((v) => typeof v === 'string' || typeof v === 'number')
      .map((v) => String(v).trim())
      .filter((v) => v.length > 0);
  }
  // A bare numeric search value (e.g. `?priority=2`) parses to a number.
  if (typeof value === 'number') {
    return [String(value)];
  }
  if (typeof value === 'string') {
    if (value.trim().length === 0) return [];
    return value
      .split(',')
      .map((v) => v.trim())
      .filter((v) => v.length > 0);
  }
  return undefined;
};

const parseSearchBoolean = (value: unknown): boolean | undefined => {
  if (value === true || value === 'true') return true;
  if (value === false || value === 'false') return false;
  return undefined;
};

export const rootRoute = createRootRoute({
  component: RootComponent,
  errorComponent: RouteErrorComponent,
});

export const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: HomeDashboard,
});

export const issuesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/issues',
  component: Issues,
  validateSearch: (search: Record<string, unknown>) => {
    const page = parseSearchNumber(search.page) ?? 1;
    const pageSize = parseSearchNumber(search.pageSize) ?? 25;
    const severity = parseSearchString(search.severity);
    const propertyId = parseSearchString(search.propertyId);
    const statusesRaw = parseSearchArray(search.statuses);
    const validStatuses = ['open', 'closed', 'not_a_bug', 'all'];
    // 'all' is a sentinel value meaning "show all statuses"
    // undefined means first visit → default to ['open']
    const statuses =
      statusesRaw === undefined ? ['open'] : statusesRaw.filter((s) => validStatuses.includes(s));
    const searchText = parseSearchString(search.search);
    const lastSeenStart = parseSearchString(search.lastSeenStart);
    const lastSeenEnd = parseSearchString(search.lastSeenEnd);
    const categoryIds = parseSearchArray(search.categoryIds);
    const credentialIds = parseSearchArray(search.credentialIds);
    const tagIds = parseSearchArray(search.tagIds);
    const includeArchived = parseSearchBoolean(search.includeArchived);

    // Parse sorting params
    const validSortFields = ['severity', 'status', 'category', 'title', 'path', 'lastSeenAt'];
    const sortByRaw = parseSearchString(search.sortBy);
    const sortBy = sortByRaw && validSortFields.includes(sortByRaw) ? sortByRaw : undefined;
    const sortOrderRaw = parseSearchString(search.sortOrder);
    const sortOrder = sortOrderRaw === 'asc' || sortOrderRaw === 'desc' ? sortOrderRaw : undefined;

    return {
      issueId: parseSearchString(search.issueId),
      page,
      pageSize,
      severity: severity ?? 'all',
      propertyId: propertyId ?? 'all',
      statuses,
      search: searchText,
      lastSeenStart,
      lastSeenEnd,
      categoryIds,
      credentialIds,
      tagIds,
      includeArchived: includeArchived === true ? true : undefined,
      sortBy,
      sortOrder,
    };
  },
});

export const prReviewsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pr-reviews',
  component: () => (
    <RequireFeature feature="prReview">
      <PrReviews />
    </RequireFeature>
  ),
  validateSearch: (search: Record<string, unknown>) => ({
    cursor: parseSearchString(search.cursor),
  }),
});

export const ticketsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/tickets',
  component: () => (
    <RequireFeature feature="tickets">
      <Tickets />
    </RequireFeature>
  ),
});

export const prReviewDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pr-reviews/$prReviewId',
  component: () => (
    <RequireFeature feature="prReview">
      <PrReviewDetail />
    </RequireFeature>
  ),
});

export const digestDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/digests/$digestId',
  component: DigestPage,
});

export const propertyDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/applications/$propertyId',
  component: PropertyDetail,
});

export const propertyDetailIndexRoute = createRoute({
  getParentRoute: () => propertyDetailRoute,
  path: '/',
  component: () => {
    const { propertyId } = useParams({ from: '/applications/$propertyId' });
    const navigate = useNavigate();
    React.useEffect(() => {
      navigate({ to: '/applications/$propertyId/settings', params: { propertyId } });
    }, [navigate, propertyId]);
    return null;
  },
});

export const propertyCodeTabRoute = createRoute({
  getParentRoute: () => propertyDetailRoute,
  path: '/code',
  component: PropertyCodeView,
});

export const propertyCredentialsTabRoute = createRoute({
  getParentRoute: () => propertyDetailRoute,
  path: '/credentials',
  component: PropertyCredentialsView,
});

export const propertyEnvironmentsTabRoute = createRoute({
  getParentRoute: () => propertyDetailRoute,
  path: '/environments',
  component: PropertyEnvironmentsView,
});

export const propertyInboxTabRoute = createRoute({
  getParentRoute: () => propertyDetailRoute,
  path: '/inbox',
  component: PropertyInboxView,
});

export const propertyContextTabRoute = createRoute({
  getParentRoute: () => propertyDetailRoute,
  path: '/context',
  component: PropertyContextView,
});

export const propertySettingsTabRoute = createRoute({
  getParentRoute: () => propertyDetailRoute,
  path: '/settings',
  component: PropertySettingsView,
});

export const propertyAdvancedTabRoute = createRoute({
  getParentRoute: () => propertyDetailRoute,
  path: '/advanced',
  component: PropertyAdvancedView,
});

propertyDetailRoute.addChildren([
  propertyDetailIndexRoute,
  propertyCodeTabRoute,
  propertyCredentialsTabRoute,
  propertyEnvironmentsTabRoute,
  propertyContextTabRoute,
  propertyInboxTabRoute,
  propertySettingsTabRoute,
  propertyAdvancedTabRoute,
]);

export const docsAssistantRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/docs-assistant',
  component: DocsAssistant,
});

export const cliLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cli-login',
  component: CliLogin,
});

export const mcpPreviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/applications/$propertyId/credentials/$credentialId/mcp-preview',
  component: TestLoginMCPPreview,
});

export type FlowsSearchParams = {
  search?: string;
  status?: string[];
  health?: string[];
  /** Legacy promotion filter (eligibility flag OFF): 'promoted' (in suites) / 'not_promoted' (paused or never promoted). */
  promotion?: string[];
  /** Filter by derived eligibility (approved + ever-passed). */
  eligible?: boolean;
  /** Filter by paused state. */
  paused?: boolean;
  lastRunStatus?: string[];
  propertyId?: string;
  tagIds?: string[];
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  pageSize?: number;
  tab?: string;
  flowType?: string[];
  createdByIds?: string[];
  /** Only flows the viewer has starred. */
  favorites?: boolean;
  /**
   * Explicit view mode. When absent, FlowList falls back to the locally
   * persisted preference (table by default), so shared links without the
   * param respect the recipient's saved view.
   */
  view?: 'grouped' | 'table';
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
};

export const validateFlowsSearch = (search: Record<string, unknown>): FlowsSearchParams => {
  const page = parseSearchNumber(search.page) ?? 1;
  const pageSize = parseSearchNumber(search.pageSize) ?? 25;
  const searchText = parseSearchString(search.search);
  const propertyId = parseSearchString(search.propertyId);
  const dateFrom = parseSearchString(search.dateFrom);
  const dateTo = parseSearchString(search.dateTo);

  const statusRaw = parseSearchArray(search.status);
  const validStatuses = ['draft', 'approved', 'deleted'];
  const status = statusRaw?.filter((s) => validStatuses.includes(s)) ?? undefined;

  const lastRunStatusRaw = parseSearchArray(search.lastRunStatus);
  const validRunStatuses = ['success', 'failed', 'running', 'queued', 'canceled', 'no_runs'];
  const lastRunStatus = lastRunStatusRaw?.filter((s) => validRunStatuses.includes(s)) ?? undefined;

  const promotionRaw = parseSearchArray(search.promotion);
  const validPromotion = ['promoted', 'not_promoted'];
  const promotion = promotionRaw?.filter((p) => validPromotion.includes(p)) ?? undefined;

  const eligible =
    search.eligible === true || search.eligible === 'true'
      ? true
      : search.eligible === false || search.eligible === 'false'
        ? false
        : undefined;
  const paused =
    search.paused === true || search.paused === 'true'
      ? true
      : search.paused === false || search.paused === 'false'
        ? false
        : undefined;

  const healthRaw = parseSearchArray(search.health);
  const validHealth = [
    'unverified',
    'never_passed',
    'passing',
    'reliable',
    'flaky',
    'failing',
    'degraded',
  ];
  const health = healthRaw?.filter((h) => validHealth.includes(h)) ?? undefined;

  const tagIds = parseSearchArray(search.tagIds);
  const tab = parseSearchString(search.tab);
  const flowType = parseSearchArray(search.flowType);
  const createdByIds = parseSearchArray(search.createdByIds);

  const favorites = search.favorites === true || search.favorites === 'true' || undefined;

  const viewRaw = parseSearchString(search.view);
  const view = viewRaw === 'grouped' || viewRaw === 'table' ? viewRaw : undefined;

  // Sort: name, or recently-run (createdAt desc is the implicit default).
  const sortByRaw = parseSearchString(search.sortBy);
  const sortBy = sortByRaw === 'name' || sortByRaw === 'lastRunAt' ? sortByRaw : undefined;
  const sortOrderRaw = parseSearchString(search.sortOrder);
  const sortOrder = sortOrderRaw === 'asc' || sortOrderRaw === 'desc' ? sortOrderRaw : undefined;

  return {
    search: searchText,
    status,
    health,
    promotion,
    eligible,
    paused,
    lastRunStatus,
    propertyId,
    tagIds,
    dateFrom,
    dateTo,
    page,
    pageSize,
    tab,
    flowType,
    createdByIds,
    favorites,
    view,
    sortBy,
    sortOrder,
  };
};

export const lookupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/lookup/$id',
  component: Lookup,
});

export const flowsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/flows',
  component: FlowList,
  validateSearch: validateFlowsSearch,
});

export const contextLibraryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/context-library',
  component: ContextLibraryPage,
});

const promptLibraryRoutes = createPromptLibraryRoutes(rootRoute);
export const promptLibraryRoute = promptLibraryRoutes.promptLibraryRoute;
export const promptLibraryDetailRoute = promptLibraryRoutes.promptLibraryDetailRoute;

export type FlowDesignerSearchParams = {
  [K in keyof ReturnType<typeof validateFlowDesignerSearch>]?: ReturnType<
    typeof validateFlowDesignerSearch
  >[K];
};

const validateFlowDesignerSearch = (search: Record<string, unknown>) => ({
  runId: parseSearchString(search.runId),
  runView: search.runView === 'live' ? ('live' as const) : undefined,
  runsPage: parseSearchNumber(search.runsPage),
  runsPageSize: parseSearchNumber(search.runsPageSize),
  optimizationPage: parseSearchNumber(search.optimizationPage),
  expandedVideo: parseSearchBoolean(search.expandedVideo) === true ? true : undefined,
  // Deep-link to an immutable workflow definition revision from Activity.
  revision: (() => {
    const revision = parseSearchNumber(search.revision);
    return revision && revision > 0 ? revision : undefined;
  })(),
  // Deep-link to a specific proposal's review (e.g. from a run's "Proposed fix"
  // callout). Opens ProposalReviewView for this proposal on load.
  proposalId: parseSearchString(search.proposalId),
  // Cache-convergence run ids are NOT workflow run ids and must never be fed
  // into `useWorkflowRun(workflowId, runId)` — that hits
  // `GET /workflows/:id/runs/:runId`, which 404s for a convergence id. Kept
  // as its own param so the Runs tab can tell which drawer to open.
  convergenceRunId: parseSearchString(search.convergenceRunId),
});

export const flowsWorkflowRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/flows/$workflowId',
  component: FlowDesigner,
  validateSearch: validateFlowDesignerSearch,
});

export const {
  sandboxDetailRoute,
  sandboxListRoute,
  sandboxV2DetailRoute,
  sandboxV2PreviewRoute,
  templateDetailRoute,
} =
  createSandboxRoutes(rootRoute);

export const { proofRoute } = createProofRoutes(rootRoute);

type AttachmentsSearchParams = {
  page?: number;
  pageSize?: number;
  search?: string;
  kind?: 'all' | 'image' | 'pdf';
};

const validateAttachmentsSearch = (search: Record<string, unknown>): AttachmentsSearchParams => ({
  page: typeof search.page === 'number' && search.page > 0 ? search.page : undefined,
  pageSize:
    typeof search.pageSize === 'number' && search.pageSize > 0 ? search.pageSize : undefined,
  search:
    typeof search.search === 'string' && search.search.trim().length > 0
      ? search.search
      : undefined,
  kind:
    search.kind === 'image' || search.kind === 'pdf' || search.kind === 'all'
      ? search.kind
      : undefined,
});

export const attachmentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/attachments',
  component: Attachments,
  validateSearch: validateAttachmentsSearch,
});

const utilitiesRoutes = createUtilitiesRoutes(rootRoute);
export const utilitiesRoute = utilitiesRoutes.utilitiesRoute;
export const utilityGistRoute = utilitiesRoutes.utilityGistRoute;

// Public, unauthenticated gist viewer. AppShell bypasses auth for `/u/g/*`.
export const publicGistRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/u/g/$id',
  component: PublicGist,
});

export type PagesSearchParams = {
  view?: 'table' | 'tree';
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
  qaStatus?: string;
  lastSeenStart?: string;
  lastSeenEnd?: string;
  credentialId?: string;
};

const validatePagesSearch = (search: Record<string, unknown>): PagesSearchParams => ({
  view: search.view === 'table' ? 'table' : undefined,
  // Handle both string (from URL) and number (from programmatic navigation)
  page:
    typeof search.page === 'number'
      ? search.page
      : typeof search.page === 'string'
        ? parseInt(search.page, 10) || undefined
        : undefined,
  pageSize:
    typeof search.pageSize === 'number'
      ? search.pageSize
      : typeof search.pageSize === 'string'
        ? parseInt(search.pageSize, 10) || undefined
        : undefined,
  sortBy: typeof search.sortBy === 'string' ? search.sortBy : undefined,
  sortOrder:
    search.sortOrder === 'asc' || search.sortOrder === 'desc' ? search.sortOrder : undefined,
  search: typeof search.search === 'string' ? search.search : undefined,
  qaStatus: typeof search.qaStatus === 'string' ? search.qaStatus : undefined,
  lastSeenStart: typeof search.lastSeenStart === 'string' ? search.lastSeenStart : undefined,
  lastSeenEnd: typeof search.lastSeenEnd === 'string' ? search.lastSeenEnd : undefined,
  credentialId: typeof search.credentialId === 'string' ? search.credentialId : undefined,
});

export const pagesLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pages',
  component: PagesLayout,
});

export const pagesIndexRoute = createRoute({
  getParentRoute: () => pagesLayoutRoute,
  path: '/',
  component: Pages,
  validateSearch: validatePagesSearch,
});

export const pagesPropertyRoute = createRoute({
  getParentRoute: () => pagesLayoutRoute,
  path: '/$propertyId',
  component: Pages,
  validateSearch: validatePagesSearch,
});

export const pageDetailRoute = createRoute({
  getParentRoute: () => pagesLayoutRoute,
  path: '/$propertyId/canonical/$canonicalPageId',
  component: PageDetail,
});

export const runsLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/runs',
  component: RunsLayout,
});

export const runsIndexRoute = createRoute({
  getParentRoute: () => runsLayoutRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/runs/test' });
  },
});

export const uxReviewRunsRoute = createRoute({
  getParentRoute: () => runsLayoutRoute,
  path: '/ux-review',
  component: () => (
    <RequireFeature feature="uxReview">
      <UxReviewPage />
    </RequireFeature>
  ),
  validateSearch: (search: Record<string, unknown>) => {
    const parseNumber = (value: unknown): number | undefined => {
      if (typeof value === 'number' && !Number.isNaN(value)) return value;
      if (typeof value === 'string') {
        const parsed = parseInt(value, 10);
        if (!Number.isNaN(parsed)) return parsed;
      }
      return undefined;
    };
    return {
      page: parseNumber(search.page) ?? 1,
      pageSize: parseNumber(search.pageSize) ?? 20,
    };
  },
});

export const uxReviewDetailRoute = createRoute({
  getParentRoute: () => runsLayoutRoute,
  path: '/ux-review/$exploratoryTestId',
  component: () => (
    <RequireFeature feature="uxReview">
      <ExploratoryTestDetail />
    </RequireFeature>
  ),
});

export const specReviewRunsRoute = createRoute({
  getParentRoute: () => runsLayoutRoute,
  path: '/spec-review',
  component: () => (
    <RequireFeature feature="specReview">
      <SpecReviewPage />
    </RequireFeature>
  ),
  validateSearch: (search: Record<string, unknown>) => {
    const parseNumber = (value: unknown): number | undefined => {
      if (typeof value === 'number' && !Number.isNaN(value)) return value;
      if (typeof value === 'string') {
        const parsed = parseInt(value, 10);
        if (!Number.isNaN(parsed)) return parsed;
      }
      return undefined;
    };
    return {
      page: parseNumber(search.page) ?? 1,
      pageSize: parseNumber(search.pageSize) ?? 20,
    };
  },
});

export const specReviewDetailRoute = createRoute({
  getParentRoute: () => runsLayoutRoute,
  path: '/spec-review/$exploratoryTestId',
  component: () => (
    <RequireFeature feature="specReview">
      <ExploratoryTestDetail />
    </RequireFeature>
  ),
});

export const testRunsRoute = createRoute({
  getParentRoute: () => runsLayoutRoute,
  path: '/test',
  component: TestRunsPage,
  validateSearch: validateTestRunsSearch,
});

export const qaRunsRoute = createRoute({
  getParentRoute: () => runsLayoutRoute,
  path: '/qa',
  component: QaRunsPage,
  validateSearch: validateQaRunsSearch,
});

export type RunDetailSearchParams = {
  page?: number;
  pageSize?: number;
};

const validateRunDetailSearch = (search: Record<string, unknown>): RunDetailSearchParams => ({
  page:
    typeof search.page === 'number'
      ? search.page
      : typeof search.page === 'string'
        ? parseInt(search.page, 10) || undefined
        : undefined,
  pageSize:
    typeof search.pageSize === 'number'
      ? search.pageSize
      : typeof search.pageSize === 'string'
        ? parseInt(search.pageSize, 10) || undefined
        : undefined,
});

export const runDetailRoute = createRoute({
  getParentRoute: () => runsLayoutRoute,
  path: '/$propertyId/$runId',
  component: RunDetail,
  validateSearch: validateRunDetailSearch,
});

export type TestRunDetailSearchParams = {
  q?: string;
  statuses?: string[];
  failuresOnly?: boolean;
  retriedOnly?: boolean;
};

export const testRunDetailRoute = createRoute({
  getParentRoute: () => runsLayoutRoute,
  path: '/test/$executionId',
  component: AgenticRunDetail,
  validateSearch: (search: Record<string, unknown>): TestRunDetailSearchParams => ({
    q: parseSearchString(search.q),
    statuses: parseSearchArray(search.statuses),
    failuresOnly: parseSearchBoolean(search.failuresOnly) === true ? true : undefined,
    retriedOnly: parseSearchBoolean(search.retriedOnly) === true ? true : undefined,
  }),
});

export type ExternalRunDetailSearchParams = {
  page: number;
  pageSize: number;
  q?: string;
  verdicts?: string[];
  /** Narrows the tests view to one area (1-based shard index); only meaningful in the grouped view. */
  area?: number;
  /**
   * Explicit results view for a multi-area run. Absent defaults to
   * 'grouped' — a single-area run ignores this and always shows the flat
   * table.
   */
  view?: 'grouped' | 'flat';
  /**
   * Free-text search over the areas grid's area labels. Deliberately
   * separate from `q` (which filters the flat per-test table server-side) —
   * this filters `areas` client-side and is never sent to the API.
   */
  areaQ?: string;
  /** Area-state filter for the areas grid, client-side only (see `areaQ`). */
  areaStates?: RunAreaState[];
};

export const validateExternalRunDetailSearch = (
  search: Record<string, unknown>
): ExternalRunDetailSearchParams => {
  const page = Number(search.page);
  const pageSize = Number(search.pageSize);
  const area = Number(search.area);
  return {
    page: Number.isInteger(page) && page > 0 ? page : 1,
    pageSize: [20, 50, 100].includes(pageSize) ? pageSize : 20,
    q: parseSearchString(search.q),
    verdicts: parseSearchArray(search.verdicts),
    area: Number.isInteger(area) && area > 0 ? area : undefined,
    view: search.view === 'flat' ? 'flat' : search.view === 'grouped' ? 'grouped' : undefined,
    areaQ: parseSearchString(search.areaQ),
    areaStates: parseSearchArray(search.areaStates)?.filter(isRunAreaState),
  };
};

export const externalRunDetailRoute = createRoute({
  getParentRoute: () => runsLayoutRoute,
  path: '/external/$runId',
  validateSearch: validateExternalRunDetailSearch,
  component: () => (
    <RequireFeature feature="externalTestSuites">
      <ExternalRunDetail />
    </RequireFeature>
  ),
});

export const externalTestItemDetailRoute = createRoute({
  getParentRoute: () => runsLayoutRoute,
  path: '/external/$runId/item/$itemId',
  component: () => (
    <RequireFeature feature="externalTestSuites">
      <ExternalTestItemDetail />
    </RequireFeature>
  ),
});

export type ExternalTestsCatalogSearchParams = {
  page?: number;
  pageSize?: number;
  search?: string;
  suiteIds?: string[];
  sourceKeys?: string[];
  providers?: string[];
  health?: string[];
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
};

const EXTERNAL_TEST_CATALOG_SORTS = [
  'displayName',
  'lastSeenAt',
  'reliability',
  'totalRuns',
] as const;
const EXTERNAL_TEST_CATALOG_PROVIDERS = ['selenium', 'playwright', 'cypress', 'junit'] as const;
const EXTERNAL_TEST_CATALOG_HEALTH = ['unverified', 'passing', 'flaky', 'failing'] as const;

export const validateExternalTestsCatalogSearch = (
  search: Record<string, unknown>
): ExternalTestsCatalogSearchParams => {
  const page = parseSearchNumber(search.page);
  const pageSize = parseSearchNumber(search.pageSize);
  const sortOrder =
    search.sortOrder === 'asc' || search.sortOrder === 'desc' ? search.sortOrder : undefined;
  const sortByRaw = parseSearchString(search.sortBy);
  const sortBy = EXTERNAL_TEST_CATALOG_SORTS.includes(
    sortByRaw as (typeof EXTERNAL_TEST_CATALOG_SORTS)[number]
  )
    ? sortByRaw
    : undefined;
  const providers = parseSearchArray(search.providers)?.filter((value) =>
    EXTERNAL_TEST_CATALOG_PROVIDERS.includes(
      value as (typeof EXTERNAL_TEST_CATALOG_PROVIDERS)[number]
    )
  );
  const health = parseSearchArray(search.health)?.filter((value) =>
    EXTERNAL_TEST_CATALOG_HEALTH.includes(value as (typeof EXTERNAL_TEST_CATALOG_HEALTH)[number])
  );
  return {
    page: typeof page === 'number' && Number.isInteger(page) && page > 0 ? page : 1,
    pageSize:
      typeof pageSize === 'number' && Number.isInteger(pageSize) && [25, 50, 100].includes(pageSize)
        ? pageSize
        : 25,
    search: parseSearchString(search.search),
    suiteIds: parseSearchArray(search.suiteIds),
    sourceKeys: parseSearchArray(search.sourceKeys),
    providers: providers?.length ? providers : undefined,
    health: health?.length ? health : undefined,
    sortBy,
    sortOrder,
  };
};

export const externalTestsCatalogRoute = createRoute({
  getParentRoute: () => runsLayoutRoute,
  path: '/external-tests',
  component: () => (
    <RequireFeature feature="externalTestSuites">
      <ExternalTestsCatalog />
    </RequireFeature>
  ),
  validateSearch: validateExternalTestsCatalogSearch,
});

export const testRunItemDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/runs/tests/$testRunId',
  component: TestRunRedirect,
});

export const runItemDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/runs/$runId',
  component: RunDetailPage,
  validateSearch: (
    search: Record<string, unknown>
  ): {
    testRunId?: string;
    workflowId?: string;
    debug?: boolean;
    debug_tab?: string;
    step?: string;
  } => ({
    testRunId: typeof search.testRunId === 'string' ? search.testRunId : undefined,
    workflowId: typeof search.workflowId === 'string' ? search.workflowId : undefined,
    debug: search.debug === true || search.debug === 'true' ? true : undefined,
    debug_tab: typeof search.debug_tab === 'string' ? search.debug_tab : undefined,
    step: typeof search.step === 'string' ? search.step : undefined,
  }),
});

export const exploratoryTestsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/exploratory',
  component: ExploratoryTestsPage,
  validateSearch: (search: Record<string, unknown>) => {
    const parseNumber = (value: unknown): number | undefined => {
      if (typeof value === 'number' && !Number.isNaN(value)) return value;
      if (typeof value === 'string') {
        const parsed = parseInt(value, 10);
        if (!Number.isNaN(parsed)) return parsed;
      }
      return undefined;
    };
    return {
      page: parseNumber(search.page) ?? 1,
      pageSize: parseNumber(search.pageSize) ?? 20,
    };
  },
});

export const validateVisualSearch = (search: Record<string, unknown>): VisualSearch => ({
  status: parseVisualStatus(parseSearchString(search.status)),
  suite: parseSearchString(search.suite),
  workflow: parseSearchString(search.workflow),
  spec: parseSearchString(search.spec),
  environment: parseSearchString(search.environment),
  source: parseVisualSource(parseSearchString(search.source)),
  execution: parseSearchString(search.execution),
  run: parseSearchString(search.run),
});

export const visualRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/visual',
  component: () => (
    <RequireFeature feature="sandboxV2">
      <RequireFeature feature="sandboxV2VisualComparison">
        <VisualPage />
      </RequireFeature>
    </RequireFeature>
  ),
  validateSearch: validateVisualSearch,
});

export const validatePerfSearch = (search: Record<string, unknown>): PerfSearch => ({
  status: parsePerfStatus(parseSearchString(search.status)),
  verdict: parsePerfVerdict(parseSearchString(search.verdict)),
});

export const perfRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/performance',
  component: () => (
    <RequireFeature feature="sandboxV2">
      <PerfPage />
    </RequireFeature>
  ),
  validateSearch: validatePerfSearch,
});

export const perfTestRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/performance/tests/$testId',
  component: () => (
    <RequireFeature feature="sandboxV2">
      <PerfTestPage />
    </RequireFeature>
  ),
});

export const perfRunRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/performance/runs/$runId',
  component: () => (
    <RequireFeature feature="sandboxV2">
      <PerfRunPage />
    </RequireFeature>
  ),
});

export const agentEvalsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/agent-evals',
  component: () => (
    <RequireFeature feature="sandboxV2">
      <AgentEvalsPage />
    </RequireFeature>
  ),
});

export const agentEvalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/agent-evals/$name',
  component: () => (
    <RequireFeature feature="sandboxV2">
      <AgentEvalPage />
    </RequireFeature>
  ),
});

export const agentEvalRunRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/agent-evals/runs/$runId',
  component: () => (
    <RequireFeature feature="sandboxV2">
      <AgentEvalRunPage />
    </RequireFeature>
  ),
});

export const visualCheckpointRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/visual/$captureId',
  component: () => (
    <RequireFeature feature="sandboxV2">
      <RequireFeature feature="sandboxV2VisualComparison">
        <VisualCheckpointPage />
      </RequireFeature>
    </RequireFeature>
  ),
});

export const exploratoryTestDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/exploratory/$exploratoryTestId',
  component: ExploratoryTestDetail,
});

// ── Suites (persisted, layered test suites) ───────────────────────────────
export const suitesLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/suites',
  component: SuitesLayout,
});

export const suitesIndexRoute = createRoute({
  getParentRoute: () => suitesLayoutRoute,
  path: '/',
  component: SuitesListPage,
});

export const suiteBuilderRoute = createRoute({
  getParentRoute: () => suitesLayoutRoute,
  path: '/$suiteId',
  component: SuiteBuilderPage,
});

// Legacy suite-run view: suite runs now surface on the standard Test Run page.
// Redirect old/bookmarked links there (the executionId is the test execution id).
export const suiteRunDetailRoute = createRoute({
  getParentRoute: () => suitesLayoutRoute,
  path: '/$suiteId/runs/$executionId',
  beforeLoad: ({ params }) => {
    throw redirect({
      to: '/runs/test/$executionId',
      params: { executionId: params.executionId },
    });
  },
});

export type ReleasesSearchParams = {
  page?: number;
  pageSize?: number;
  propertyId?: string;
};

const validateReleasesSearch = (search: Record<string, unknown>): ReleasesSearchParams => {
  const parseNumber = (value: unknown): number | undefined => {
    if (typeof value === 'number' && !Number.isNaN(value)) return value;
    if (typeof value === 'string') {
      const parsed = parseInt(value, 10);
      if (!Number.isNaN(parsed)) return parsed;
    }
    return undefined;
  };
  const parseString = (value: unknown): string | undefined =>
    typeof value === 'string' && value.trim().length > 0 ? value : undefined;

  return {
    page: parseNumber(search.page) ?? 1,
    pageSize: parseNumber(search.pageSize) ?? 20,
    propertyId: parseString(search.propertyId),
  };
};

// Release Timeline routes — ReleasesLayout is a bare outlet host; navigation
// lives in the breadcrumb dropdown, not a subnav.
export const releasesLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/releases',
  component: ReleasesLayout,
});

export const releasesIndexRoute = createRoute({
  getParentRoute: () => releasesLayoutRoute,
  path: '/',
  component: ReleasesIndex,
});

export type ReleasePropertySearchParams = {
  /** The run selected in the timeline's master-detail pane. No `?runId=` means the default selection (see `pickDefaultRunId`), not "no run". */
  runId?: string;
};

const validateReleasePropertySearch = (
  search: Record<string, unknown>
): ReleasePropertySearchParams => ({
  runId: parseSearchString(search.runId),
});

export const releasePropertyRoute = createRoute({
  getParentRoute: () => releasesLayoutRoute,
  path: '/$propertyId',
  component: ReleasePropertyTimeline,
  validateSearch: validateReleasePropertySearch,
});

// The run detail page was folded into the timeline's master-detail pane
// (see `ReleasePropertyTimeline`/`ReleaseRunPane`) — this route now only
// exists to redirect old/bookmarked links (and the URLs the GitHub check run
// and Slack integrations still emit, e.g.
// `apps/api/src/integrations/github/release-qa-check-run.ts`) onto the
// timeline with the run preselected via `?runId=`.
export const releaseRunDetailRoute = createRoute({
  getParentRoute: () => releasesLayoutRoute,
  path: '/$propertyId/runs/$runId',
  beforeLoad: ({ params }) => {
    throw redirect({
      to: '/releases/$propertyId',
      params: { propertyId: params.propertyId },
      search: { runId: params.runId },
      replace: true,
    });
  },
});

// Product Map — grouped sortable list editor for areas → capabilities, plus
// the Explore view (List | Explore switcher, decision 2026-09-17: Explore is
// a view of the Product Map, not its own page).
export const productMapRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/product-map',
  component: ProductMapPage,
  validateSearch: validateProductMapSearch,
});

// Cycle detail — reached from the Explore view's map/latest/recent cycles.
// Exploration-gated like the view switcher itself since it has no chrome of
// its own to hide the gate behind.
export const productMapCycleDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/product-map/cycles/$cycleId',
  component: () => {
    const { cycleId } = useParams({ from: '/product-map/cycles/$cycleId' });
    return (
      <RequireFeature feature="exploration">
        <CycleDetailPage cycleId={cycleId} />
      </RequireFeature>
    );
  },
});

// Legacy /explore* redirects (2026-09-17) — Explore moved onto /product-map
// as a view rather than its own route tree. Kept so old bookmarks and Slack
// digest links still resolve.
export const exploreLegacyRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/explore',
  beforeLoad: () => {
    throw redirect({ to: '/product-map', search: { view: 'explore' }, replace: true });
  },
});

export const exploreCycleLegacyRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/explore/cycles/$cycleId',
  beforeLoad: ({ params }) => {
    throw redirect({
      to: '/product-map/cycles/$cycleId',
      params: { cycleId: params.cycleId },
      replace: true,
    });
  },
});

// Boards layout (Agent Tasks + Product Board)
export const boardsLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/boards',
  component: BoardsLayout,
});

export const boardsIndexRoute = createRoute({
  getParentRoute: () => boardsLayoutRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/boards/tasks' });
  },
});

// Agent Tasks is a child of /boards
export const tasksIndexRoute = createRoute({
  getParentRoute: () => boardsLayoutRoute,
  path: '/tasks',
  component: TasksIndex,
  validateSearch: (search: Record<string, unknown>) => ({
    status: parseSearchString(search.status),
    triggerType: parseSearchString(search.triggerType),
    page: parseSearchString(search.page),
    taskId: parseSearchString(search.taskId),
    timeframe: parseSearchString(search.timeframe),
    agentSubmitted: parseSearchString(search.agentSubmitted),
    search: parseSearchString(search.search),
    tagIds: parseSearchArray(search.tagIds),
    excludeTagIds: parseSearchArray(search.excludeTagIds),
    priority: parseSearchArray(search.priority),
    customer: parseSearchString(search.customer),
    createdBy: parseSearchString(search.createdBy),
  }),
});

export const boardsProductsRoute = createRoute({
  getParentRoute: () => boardsLayoutRoute,
  path: '/products',
  component: ProductBoardPage,
  validateSearch: (search: Record<string, unknown>) => ({
    // Accept boolean true (programmatic navigate) or string "true" (typed URL).
    includeDeprecated:
      search.includeDeprecated === true || search.includeDeprecated === 'true' ? true : undefined,
    itemId: parseSearchString(search.itemId),
    search: parseSearchString(search.search),
    priority: parseSearchArray(search.priority),
    customer: parseSearchString(search.customer),
  }),
});

export const boardsAccountsRoute = createRoute({
  getParentRoute: () => boardsLayoutRoute,
  path: '/accounts',
  component: AccountsBoardPage,
  validateSearch: (search: Record<string, unknown>) => ({
    cardId: parseSearchString(search.cardId),
    search: parseSearchString(search.search),
  }),
});

// Legacy /tasks redirect → /boards/tasks
export const tasksLegacyRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/tasks',
  beforeLoad: () => {
    throw redirect({ to: '/boards/tasks' });
  },
});

// Legacy /properties* → /applications* (cosmetic rename, 2026-06). The canonical
// URL is now /applications; code/DB/API still use "property". These keep old
// bookmarks/links working. The splat form rewrites the leading path segment via
// `location.href` so deep paths, query strings, and hash are all preserved.
//
// Rewrites a legacy /properties href (path + search + hash, no origin) to its
// /applications equivalent. Handles both the root list/detail tree and the
// /test-data subtree; leaves non-property paths untouched.
export function rewriteLegacyPropertiesHref(href: string): string {
  return href
    .replace(/^\/test-data\/properties(?=\/|$)/, '/test-data/applications')
    .replace(/^\/properties(?=\/|$)/, '/applications');
}

export const propertiesListLegacyRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/properties',
  beforeLoad: () => {
    throw redirect({ to: '/applications', replace: true });
  },
});

export const propertiesLegacyRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/properties/$',
  beforeLoad: ({ location }) => {
    throw redirect({ href: rewriteLegacyPropertiesHref(location.href), replace: true });
  },
});

export const taskDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/tasks/$id',
  component: TaskDetail,
});

// Legacy routes for backward compatibility
export const releasesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/releases-legacy',
  component: Releases,
  validateSearch: validateReleasesSearch,
});

export type ReleaseDetailSearchParams = {
  tab?: 'testers' | 'regressions' | 'report';
};

const validateReleaseDetailSearch = (
  search: Record<string, unknown>
): ReleaseDetailSearchParams => {
  const tab = search.tab;
  const validTabs = ['testers', 'regressions', 'report'];
  if (typeof tab === 'string' && validTabs.includes(tab)) {
    return { tab: tab as ReleaseDetailSearchParams['tab'] };
  }
  return { tab: 'testers' };
};

export const releaseDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/releases-legacy/$releaseId',
  component: ReleaseDetail,
  validateSearch: validateReleaseDetailSearch,
});

export const apiCatalogRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/api-requests',
  component: ApiCatalog,
  validateSearch: (search: Record<string, unknown>) => {
    const parseNumber = (value: unknown): number | undefined => {
      if (typeof value === 'number' && !Number.isNaN(value)) return value;
      if (typeof value === 'string') {
        const parsed = parseInt(value, 10);
        if (!Number.isNaN(parsed)) return parsed;
      }
      return undefined;
    };

    const parseString = (value: unknown): string | undefined =>
      typeof value === 'string' && value.trim().length > 0 ? value : undefined;

    const parseArray = (value: unknown): string[] | undefined => {
      if (Array.isArray(value)) {
        const filtered = value.filter((v) => typeof v === 'string' && v.trim().length > 0);
        return filtered.length > 0 ? filtered : undefined;
      }
      if (typeof value === 'string') {
        const parts = value
          .split(',')
          .map((v) => v.trim())
          .filter((v) => v.length > 0);
        return parts.length > 0 ? parts : undefined;
      }
      return undefined;
    };

    const page = parseNumber(search.page) ?? 1;
    const pageSize = parseNumber(search.pageSize) ?? 25;
    const requestId = parseString(search.requestId);
    const searchText = parseString(search.search);
    const domains = parseArray(search.domains);
    const methods = parseArray(search.methods)?.map((m) => m.toUpperCase());
    const credentialIds = parseArray(search.credentialIds);
    const lastSeenStart = parseString(search.lastSeenStart);
    const lastSeenEnd = parseString(search.lastSeenEnd);

    const statusesRaw = parseArray(search.statuses);
    const validStatuses = ['2xx', '4xx', '5xx'];
    const statuses = statusesRaw?.filter((s) => validStatuses.includes(s)) ?? undefined;

    return {
      page,
      pageSize,
      requestId,
      search: searchText,
      domains,
      methods,
      statuses,
      credentialIds,
      lastSeenStart,
      lastSeenEnd,
    };
  },
});

export const apiCatalogDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/api-requests/$requestId',
  component: ApiRequestDetailPage,
});

export const knownPageAnalysisDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/applications/$propertyId/known-page-analysis/$jobId',
  component: KnownPageAnalysisDetail,
});

// Test Data routes with nested layout
export const testDataLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/test-data',
  component: TestDataLayout,
});

export const testDataIndexRoute = createRoute({
  getParentRoute: () => testDataLayoutRoute,
  path: '/',
  component: TestDataPage,
});

export const testDataSectionRoute = createRoute({
  getParentRoute: () => testDataLayoutRoute,
  path: '/$section',
  component: TestDataPage,
});

// Test users moved under each application's "Test Users" tab (2026-06);
// the old top-level section redirects to the applications list.
export const tdTestUsersRedirectRoute = createRoute({
  getParentRoute: () => testDataLayoutRoute,
  path: '/test-users',
  beforeLoad: () => {
    throw redirect({ to: '/test-data', replace: true });
  },
});

// Legacy /test-data/properties/* → /test-data/applications/* (cosmetic rename, 2026-06).
export const tdPropertiesLegacyRedirectRoute = createRoute({
  getParentRoute: () => testDataLayoutRoute,
  path: '/properties/$',
  beforeLoad: ({ location }) => {
    throw redirect({ href: rewriteLegacyPropertiesHref(location.href), replace: true });
  },
});

// Test Data > Property Detail (child of testDataLayoutRoute)
export const tdPropertyDetailRoute = createRoute({
  getParentRoute: () => testDataLayoutRoute,
  path: '/applications/$propertyId',
  component: TestDataPropertyDetail,
});

export const tdPropertyDetailIndexRoute = createRoute({
  getParentRoute: () => tdPropertyDetailRoute,
  path: '/',
  beforeLoad: ({ params }) => {
    throw redirect({
      to: '/test-data/applications/$propertyId/settings',
      params: { propertyId: params.propertyId },
    });
  },
});

export const tdPropertyCodeRoute = createRoute({
  getParentRoute: () => tdPropertyDetailRoute,
  path: '/code',
  component: TDPropertyCodeView,
});

export const tdPropertyCredentialsRoute = createRoute({
  getParentRoute: () => tdPropertyDetailRoute,
  path: '/credentials',
  component: TDPropertyCredentialsView,
});

export const tdPropertyEnvironmentsRoute = createRoute({
  getParentRoute: () => tdPropertyDetailRoute,
  path: '/environments',
  component: TDPropertyEnvironmentsView,
});

export const tdPropertyInboxRoute = createRoute({
  getParentRoute: () => tdPropertyDetailRoute,
  path: '/inbox',
  component: TDPropertyInboxView,
});

export const tdPropertyContextRoute = createRoute({
  getParentRoute: () => tdPropertyDetailRoute,
  path: '/context',
  component: TDPropertyContextView,
});

export const tdPropertySettingsRoute = createRoute({
  getParentRoute: () => tdPropertyDetailRoute,
  path: '/settings',
  component: TDPropertySettingsView,
});

export const tdPropertyAdvancedRoute = createRoute({
  getParentRoute: () => tdPropertyDetailRoute,
  path: '/advanced',
  component: TDPropertyAdvancedView,
});

tdPropertyDetailRoute.addChildren([
  tdPropertyDetailIndexRoute,
  tdPropertyCodeRoute,
  tdPropertyCredentialsRoute,
  tdPropertyEnvironmentsRoute,
  tdPropertyContextRoute,
  tdPropertyInboxRoute,
  tdPropertySettingsRoute,
  tdPropertyAdvancedRoute,
]);

// Settings routes with nested layout
export const settingsLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  component: SettingsLayout,
});

export const settingsIndexRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/',
  component: Settings,
});

export const settingsSectionRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/$section',
  component: Settings,
});

// Superadmin routes with nested layout
export const superadminLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/superadmin',
  component: SuperadminLayout,
});

export const superadminDashboardRoute = createRoute({
  getParentRoute: () => superadminLayoutRoute,
  path: '/',
  component: SuperadminDashboard,
});

export const superadminJobsRoute = createRoute({
  getParentRoute: () => superadminLayoutRoute,
  path: '/jobs',
  component: SuperadminJobs,
});

export type AgentRunsSearchParams = {
  label?: string;
  orgId?: string;
  jobId?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
};

const validateAgentRunsSearch = (search: Record<string, unknown>): AgentRunsSearchParams => {
  const parseString = (value: unknown): string | undefined =>
    typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;
  const parseNumber = (value: unknown): number | undefined => {
    if (typeof value === 'number' && !Number.isNaN(value)) return value;
    if (typeof value === 'string') {
      const parsed = parseInt(value, 10);
      if (!Number.isNaN(parsed)) return parsed;
    }
    return undefined;
  };
  const parseDate = (value: unknown): string | undefined => {
    if (typeof value !== 'string' || value.trim().length === 0) return undefined;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : value.trim();
  };

  return {
    label: parseString(search.label),
    orgId: parseString(search.orgId),
    jobId: parseString(search.jobId),
    status: parseString(search.status),
    startDate: parseDate(search.startDate),
    endDate: parseDate(search.endDate),
    page: parseNumber(search.page),
  };
};

export const superadminAgentRunsRoute = createRoute({
  getParentRoute: () => superadminLayoutRoute,
  path: '/agent-runs',
  component: SuperadminAgentRuns,
  validateSearch: validateAgentRunsSearch,
});

export const superadminMetricsRoute = createRoute({
  getParentRoute: () => superadminLayoutRoute,
  path: '/metrics',
  component: SuperadminMetrics,
});

export const superadminCacheTelemetryRoute = createRoute({
  getParentRoute: () => superadminLayoutRoute,
  path: '/cache-telemetry',
  component: CacheTelemetryDashboard,
  validateSearch: (search: Record<string, unknown>) => ({
    days: parseSearchNumber(search.days) ?? 7,
    workflowId: parseSearchString(search.workflowId),
  }),
});

export const superadminVerificationsRoute = createRoute({
  getParentRoute: () => superadminLayoutRoute,
  path: '/verifications',
  component: SuperadminVerifications,
});

export const superadminScheduledRoute = createRoute({
  getParentRoute: () => superadminLayoutRoute,
  path: '/scheduled',
  component: SuperadminScheduled,
});

export const superadminFeatureFlagsRoute = createRoute({
  getParentRoute: () => superadminLayoutRoute,
  path: '/feature-flags',
  component: SuperadminFeatureFlags,
});

export const superadminKnobsRoute = createRoute({
  getParentRoute: () => superadminLayoutRoute,
  path: '/knobs',
  component: SuperadminKnobs,
});

export const superadminOrgQuotasRoute = createRoute({
  getParentRoute: () => superadminLayoutRoute,
  path: '/org-quotas',
  component: SuperadminOrgQuotas,
});

export const superadminTestTemplatesRoute = createRoute({
  getParentRoute: () => superadminLayoutRoute,
  path: '/test-templates',
  component: SuperadminTestTemplates,
});

export const superadminTraceUploadsRoute = createRoute({
  getParentRoute: () => superadminLayoutRoute,
  path: '/trace-uploads',
  component: SuperadminTraceUploads,
});

export const superadminDedupRoute = createRoute({
  getParentRoute: () => superadminLayoutRoute,
  path: '/dedup',
  component: SuperadminDedup,
});

export const superadminAboutRoute = createRoute({
  getParentRoute: () => superadminLayoutRoute,
  path: '/about',
  component: SuperadminAbout,
});

export const superadminIssueReviewsRoute = createRoute({
  getParentRoute: () => superadminLayoutRoute,
  path: '/issue-reviews',
  component: SuperadminIssueReviews,
});

export const validateBenchmarkSearch = (search: Record<string, unknown>) => ({
  days: parseSearchNumber(search.days) ?? 30,
  suiteId: parseSearchString(search.suiteId),
  workflows: parseSearchArray(search.workflows),
  trigger: parseSearchString(search.trigger),
  configId: parseSearchString(search.configId),
  runs: parseSearchString(search.runs),
});

export const superadminBenchmarkRoute = createRoute({
  getParentRoute: () => superadminLayoutRoute,
  path: '/benchmark',
  component: BenchmarkPage,
  validateSearch: validateBenchmarkSearch,
});

export const superadminBenchmarkConfigsRoute = createRoute({
  getParentRoute: () => superadminLayoutRoute,
  path: '/benchmark/configs',
  component: SuperadminBenchmarkConfigs,
});

export type SuperadminOrganizationsSearchParams = {
  page?: number;
  pageSize?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: string;
};

const validateOrganizationsSearch = (
  search: Record<string, unknown>
): SuperadminOrganizationsSearchParams => {
  const parseString = (value: unknown): string | undefined =>
    typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;
  const parseNumber = (value: unknown): number | undefined => {
    if (typeof value === 'number' && !Number.isNaN(value)) return value;
    if (typeof value === 'string') {
      const parsed = parseInt(value, 10);
      if (!Number.isNaN(parsed)) return parsed;
    }
    return undefined;
  };

  return {
    page: parseNumber(search.page),
    pageSize: parseNumber(search.pageSize),
    search: parseString(search.search),
    sortBy: parseString(search.sortBy),
    sortOrder: parseString(search.sortOrder),
  };
};

export const superadminOrganizationsRoute = createRoute({
  getParentRoute: () => superadminLayoutRoute,
  path: '/organizations',
  component: SuperadminOrganizations,
  validateSearch: validateOrganizationsSearch,
});

export const superadminUsersRoute = createRoute({
  getParentRoute: () => superadminLayoutRoute,
  path: '/users',
  component: SuperadminUsers,
});

export type SuperadminMarketingLeadsSearchParams = {
  page?: number;
  pageSize?: number;
  search?: string;
};

const validateMarketingLeadsSearch = (
  search: Record<string, unknown>
): SuperadminMarketingLeadsSearchParams => ({
  page: parseSearchNumber(search.page),
  pageSize: parseSearchNumber(search.pageSize),
  search: parseSearchString(search.search),
});

export const superadminMarketingLeadsRoute = createRoute({
  getParentRoute: () => superadminLayoutRoute,
  path: '/marketing-leads',
  component: SuperadminMarketingLeads,
  validateSearch: validateMarketingLeadsSearch,
});

export const superadminWorkflowsRoute = createRoute({
  getParentRoute: () => superadminLayoutRoute,
  path: '/workflows',
  component: SuperadminWorkflows,
});

export const superadminHintsRoute = createRoute({
  getParentRoute: () => superadminLayoutRoute,
  path: '/hints',
  component: SuperadminHints,
});

export const superadminSupportRoute = createRoute({
  getParentRoute: () => superadminLayoutRoute,
  path: '/support',
  component: SuperadminSupport,
});

export const superadminDeploymentAlertsRoute = createRoute({
  getParentRoute: () => superadminLayoutRoute,
  path: '/deployment-alerts',
  component: SuperadminDeploymentAlerts,
});

export const superadminBuilderSessionsRoute = createRoute({
  getParentRoute: () => superadminLayoutRoute,
  path: '/builder-sessions',
  component: SuperadminBuilderSessions,
});

const validateAuditLogSearch = (search: Record<string, unknown>) => {
  const parseString = (value: unknown): string | undefined => {
    if (typeof value === 'string' && value.trim().length > 0) return value.trim();
    return undefined;
  };
  const parseNumber = (value: unknown): number | undefined => {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const parsed = parseInt(value, 10);
      if (!Number.isNaN(parsed)) return parsed;
    }
    return undefined;
  };

  return {
    orgId: parseString(search.orgId),
    workflowId: parseString(search.workflowId),
    runId: parseString(search.runId),
    agentType: parseString(search.agentType),
    source: parseString(search.source),
    actionType: parseString(search.actionType),
    from: parseString(search.from),
    to: parseString(search.to),
    page: parseNumber(search.page),
    pageSize: parseNumber(search.pageSize),
  };
};

export const superadminAuditLogRoute = createRoute({
  getParentRoute: () => superadminLayoutRoute,
  path: '/audit-log',
  component: SuperadminAuditLog,
  validateSearch: validateAuditLogSearch,
});

export const superadminTokenUsageRoute = createRoute({
  getParentRoute: () => superadminLayoutRoute,
  path: '/token-usage',
  component: SuperadminTokenUsage,
  validateSearch: (search: Record<string, unknown>) => {
    const startDate = parseSearchIsoDate(search.startDate);
    const endDate = parseSearchIsoDate(search.endDate);
    // Both must be valid and start must precede end; otherwise discard both
    const validRange = startDate && endDate && new Date(startDate) <= new Date(endDate);
    return {
      orgId: parseSearchString(search.orgId),
      label: parseSearchString(search.label),
      modelId: parseSearchString(search.modelId),
      platform: ['openai', 'anthropic', 'bedrock', 'azure', 'google'].includes(search.platform as string)
        ? (search.platform as string)
        : undefined,
      days: parseSearchNumber(search.days) ?? 30,
      startDate: validRange ? startDate : undefined,
      endDate: validRange ? endDate : undefined,
      reportDate: parseSearchIsoDate(search.reportDate)?.slice(0, 10),
      groupBy: ['org', 'label', 'model'].includes(search.groupBy as string)
        ? (search.groupBy as string)
        : 'org',
      orgViewMode: ['job-type', 'totals'].includes(search.orgViewMode as string)
        ? (search.orgViewMode as 'job-type' | 'totals')
        : 'job-type',
    };
  },
});

const VALID_USER_EVENTS_TABS = ['explore', 'dashboards', 'slack'] as const;
type UserEventsTab = (typeof VALID_USER_EVENTS_TABS)[number];

export const superadminUserEventsRoute = createRoute({
  getParentRoute: () => superadminLayoutRoute,
  path: '/user-events',
  component: SuperadminUserEvents,
  validateSearch: (search: Record<string, unknown>) => {
    const tabRaw = parseSearchString(search.tab);
    const tab: UserEventsTab = (VALID_USER_EVENTS_TABS as readonly string[]).includes(tabRaw ?? '')
      ? (tabRaw as UserEventsTab)
      : 'explore';
    return {
      tab,
      dashboard: parseSearchString(search.dashboard),
    };
  },
});

const authRoutes = createAuthRoutes(rootRoute);
export const inviteAcceptRoute = authRoutes.inviteAcceptRoute;
export const connectMcpRoute = authRoutes.connectMcpRoute;
export const connectRoute = authRoutes.connectRoute;
export const loginRoute = authRoutes.loginRoute;
export const mfaChallengeRoute = authRoutes.mfaChallengeRoute;
export const slackLinkRoute = authRoutes.slackLinkRoute;
export const signupRoute = authRoutes.signupRoute;
export const verifyEmailRoute = authRoutes.verifyEmailRoute;
export const forgotPasswordRoute = authRoutes.forgotPasswordRoute;
export const resetPasswordRoute = authRoutes.resetPasswordRoute;

// Custom search stringifier that serializes arrays as comma-separated values
const stringifySearchWith = (search: Record<string, unknown>): string => {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(search)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      if (value.length > 0) params.set(key, value.join(','));
    } else if (typeof value === 'boolean') {
      params.set(key, String(value));
    } else if (typeof value === 'number') {
      params.set(key, String(value));
    } else if (typeof value === 'string' && value.length > 0) {
      params.set(key, value);
    }
  }
  const str = params.toString();
  return str ? `?${str}` : '';
};

export const router = createRouter({
  stringifySearch: stringifySearchWith,
  routeTree: rootRoute.addChildren([
    homeRoute,
    lookupRoute,
    docsAssistantRoute,
    cliLoginRoute,
    propertyDetailRoute,
    mcpPreviewRoute,
    flowsRoute,
    contextLibraryRoute,
    promptLibraryRoute,
    promptLibraryDetailRoute,
    flowsWorkflowRoute,
    sandboxDetailRoute,
    sandboxListRoute,
    sandboxV2DetailRoute,
    sandboxV2PreviewRoute,
    templateDetailRoute,
    proofRoute,
    attachmentsRoute,
    utilitiesRoute,
    utilityGistRoute,
    publicGistRoute,
    pagesLayoutRoute.addChildren([pagesIndexRoute, pagesPropertyRoute, pageDetailRoute]),
    runsLayoutRoute.addChildren([
      runsIndexRoute,
      uxReviewRunsRoute,
      uxReviewDetailRoute,
      specReviewRunsRoute,
      specReviewDetailRoute,
      testRunsRoute,
      qaRunsRoute,
      runDetailRoute,
      testRunDetailRoute,
      externalRunDetailRoute,
      externalTestItemDetailRoute,
      externalTestsCatalogRoute,
    ]),
    exploratoryTestsRoute,
    exploratoryTestDetailRoute,
    visualRoute,
    visualCheckpointRoute,
    perfRoute,
    perfTestRoute,
    perfRunRoute,
    agentEvalsRoute,
    agentEvalRoute,
    agentEvalRunRoute,
    suitesLayoutRoute.addChildren([suitesIndexRoute, suiteBuilderRoute, suiteRunDetailRoute]),
    testRunItemDetailRoute,
    runItemDetailRoute,
    releasesLayoutRoute.addChildren([
      releasesIndexRoute,
      releasePropertyRoute,
      releaseRunDetailRoute,
    ]),
    productMapRoute,
    productMapCycleDetailRoute,
    exploreLegacyRedirectRoute,
    exploreCycleLegacyRedirectRoute,
    boardsLayoutRoute.addChildren([
      boardsIndexRoute,
      tasksIndexRoute,
      boardsProductsRoute,
      boardsAccountsRoute,
    ]),
    tasksLegacyRedirectRoute,
    propertiesListLegacyRedirectRoute,
    propertiesLegacyRedirectRoute,
    taskDetailRoute,
    releasesRoute,
    releaseDetailRoute,
    knownPageAnalysisDetailRoute,
    issuesRoute,
    prReviewsRoute,
    ticketsRoute,
    prReviewDetailRoute,
    digestDetailRoute,
    createRoute({ getParentRoute: () => rootRoute, path: '/applications', component: Properties }),
    testDataLayoutRoute.addChildren([
      testDataIndexRoute,
      tdPropertyDetailRoute,
      tdPropertiesLegacyRedirectRoute,
      tdTestUsersRedirectRoute,
      testDataSectionRoute,
    ]),
    settingsLayoutRoute.addChildren([settingsIndexRoute, settingsSectionRoute]),
    superadminLayoutRoute.addChildren([
      superadminDashboardRoute,
      superadminJobsRoute,
      superadminOrganizationsRoute,
      superadminUsersRoute,
      superadminMarketingLeadsRoute,
      superadminAgentRunsRoute,
      superadminBenchmarkRoute,
      superadminBenchmarkConfigsRoute,
      superadminMetricsRoute,
      superadminCacheTelemetryRoute,
      superadminVerificationsRoute,
      superadminScheduledRoute,
      superadminWorkflowsRoute,
      superadminFeatureFlagsRoute,
      superadminKnobsRoute,
      superadminOrgQuotasRoute,
      superadminTestTemplatesRoute,
      superadminTraceUploadsRoute,
      superadminDedupRoute,
      superadminIssueReviewsRoute,
      superadminAboutRoute,
      superadminHintsRoute,
      superadminSupportRoute,
      superadminDeploymentAlertsRoute,
      superadminBuilderSessionsRoute,
      superadminAuditLogRoute,
      superadminTokenUsageRoute,
      superadminUserEventsRoute,
    ]),
    inviteAcceptRoute,
    connectMcpRoute,
    connectRoute,
    loginRoute,
    mfaChallengeRoute,
    slackLinkRoute,
    signupRoute,
    verifyEmailRoute,
    forgotPasswordRoute,
    resetPasswordRoute,
    apiCatalogRoute,
    apiCatalogDetailRoute,
    createRoute({ getParentRoute: () => rootRoute, path: '/debug', component: Debug }),
    createRoute({ getParentRoute: () => rootRoute, path: '/ui-demo', component: UIDemo }),
    createRoute({
      getParentRoute: () => rootRoute,
      path: '/storage-state-demo',
      component: StorageStateDemo,
    }),
    createRoute({
      getParentRoute: () => rootRoute,
      path: '/table-pattern-demo',
      component: TablePatternDemo,
    }),
  ]),
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
